import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import { TrackerDataType } from '../../shared/constants/constants';
import { calendarUiPalette } from './calendarPalette';

export type CalendarTopBarView =
  | TrackerDataType.MEDIA_INTERACTION
  | TrackerDataType.SERVICE
  | TrackerDataType.ENGAGEMENT
  | TrackerDataType.CONTENT_LOG;

interface CalendarCategoryOption {
  key: CalendarTopBarView;
  label: string;
  color: string;
}

interface CalendarCategoryBarProps {
  categories: readonly CalendarCategoryOption[];
  selectedViews: readonly CalendarTopBarView[];
  onToggleView: (view: CalendarTopBarView) => void;
}

export default function CalendarCategoryBar({
  categories,
  selectedViews,
  onToggleView,
}: Readonly<CalendarCategoryBarProps>) {
  const selectorGap = 2.25;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: selectorGap,
        width: '100%',
        minWidth: 0,
        minHeight: 40,
        px: 2.5,
        py: 1,
        borderRadius: '10px',
        border: `1px solid ${calendarUiPalette.categoryBar.containerBorder}`,
        background: calendarUiPalette.categoryBar.containerBackground,
        boxShadow: calendarUiPalette.categoryBar.containerShadow,
        boxSizing: 'border-box',
      }}
    >
      <Typography
        component="span"
        sx={{
          flexShrink: 0,
          color: calendarUiPalette.categoryBar.headingText,
          fontSize: '0.7rem',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          lineHeight: 1,
        }}
      >
        Visible Logs
      </Typography>
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        sx={{
          borderColor: calendarUiPalette.categoryBar.unselectedIconBorder,
          opacity: 0.95,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: selectorGap,
          minWidth: 0,
          flex: '1 1 auto',
          overflow: 'hidden',
        }}
      >
        {categories.map((option) => {
          const isSelected = selectedViews.includes(option.key);
          const isOnlySelectedView = isSelected && selectedViews.length === 1;

          return (
            <Tooltip key={option.key} title="" disableHoverListener>
              <Box component="span" sx={{ minWidth: 0, display: 'inline-flex', alignItems: 'center' }}>
                <Box
                  component="button"
                  type="button"
                  disabled={isOnlySelectedView}
                  onClick={() => onToggleView(option.key)}
                  data-cy={`calendar-category-toggle-${option.key}`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    px: 0.2,
                    py: 0.35,
                    border: 0,
                    backgroundColor: 'transparent',
                    color: isSelected
                      ? calendarUiPalette.eventModal.primaryText
                      : calendarUiPalette.eventModal.neutralChipText,
                    cursor: isOnlySelectedView ? 'default' : 'pointer',
                    minHeight: 24,
                    fontFamily: '"Roboto", sans-serif',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    lineHeight: 1.2,
                    letterSpacing: 'normal',
                    minWidth: 0,
                    flexShrink: 0,
                    opacity: isOnlySelectedView ? 0.92 : 1,
                    transition: 'color 0.2s ease, opacity 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: calendarUiPalette.eventModal.primaryText,
                    },
                    '&:disabled': {
                      color: calendarUiPalette.eventModal.primaryText,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 16,
                      height: 16,
                      borderRadius: '5px',
                      flexShrink: 0,
                      backgroundColor: isSelected ? option.color : calendarUiPalette.categoryBar.iconBackground,
                      border: isSelected
                        ? `1px solid ${option.color}`
                        : `1px solid ${calendarUiPalette.categoryBar.unselectedIconBorder}`,
                      boxShadow: isSelected ? calendarUiPalette.categoryBar.selectedIconInsetShadow : 'none',
                    }}
                  >
                    {isSelected ? (
                      <CheckIcon
                        sx={{
                          fontSize: 11,
                          color: calendarUiPalette.categoryBar.selectedIconForeground,
                        }}
                      />
                    ) : null}
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                      lineHeight: 'inherit',
                      letterSpacing: 'inherit',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
